import React from 'react';
import { useTranslation } from 'react-i18next';
import { Check } from 'shared/assets/icons';
import { Icon } from 'shared/components/ui/icon';
import {
  PopupMenu,
  PopupMenuButton,
  PopupMenuList,
  PopupMenuListItem,
} from 'shared/components/ui/popup-menu';
import { useKeysDown } from 'shared/hooks/use-keys-down';
import { ImpactLevel } from 'shared/types/goal';

export type ImpactPickerMenuProps = Omit<
  React.ComponentProps<typeof PopupMenu>,
  'children' | 'onOutside'
> & {
  value?: ImpactLevel | null;
  onClose: () => void;
  onChange: (value: ImpactLevel | null) => void;
};

export const ImpactPickerMenu: React.FC<ImpactPickerMenuProps> = ({
  value,
  onChange,
  onClose,
  ...rest
}) => {
  const { t } = useTranslation();
  const menuRef = React.useRef<HTMLDivElement>(null);

  const onHigh = () => {
    onChange(ImpactLevel.High);
    onClose();
  };

  const onMedium = () => {
    onChange(ImpactLevel.Medium);
    onClose();
  };

  const onLow = () => {
    onChange(ImpactLevel.Low);
    onClose();
  };

  const onNone = () => {
    onChange(null);
    onClose();
  };

  const focusItem = React.useCallback((direction: 'next' | 'previous') => {
    if (!menuRef.current) {
      return;
    }

    const buttons = Array.from(
      menuRef.current.querySelectorAll('button:not([disabled])'),
    ) as HTMLElement[];

    if (!buttons.length) {
      return;
    }

    const currentIndex = buttons.indexOf(document.activeElement as HTMLElement);
    const nextIndex =
      direction === 'next'
        ? (currentIndex + 1) % buttons.length
        : (currentIndex - 1 + buttons.length) % buttons.length;

    buttons[nextIndex]?.focus();
  }, []);

  useKeysDown(
    ['ArrowDown'],
    (e) => {
      e.stopPropagation();
      e.preventDefault();
      focusItem('next');
    },
    { capture: true },
  );

  useKeysDown(
    ['ArrowUp'],
    (e) => {
      e.stopPropagation();
      e.preventDefault();
      focusItem('previous');
    },
    { capture: true },
  );

  return (
    <PopupMenu ref={menuRef} onOutside={onClose} {...rest}>
      <PopupMenuList>
        <PopupMenuListItem>
          <PopupMenuButton
            onClick={onHigh}
            start={
              value === ImpactLevel.High ? <Icon icon={Check} /> : undefined
            }
            indentationLevel={value !== ImpactLevel.High ? 1 : 0}
          >
            {t('goal.impact.options.high.label')}
          </PopupMenuButton>
        </PopupMenuListItem>
        <PopupMenuListItem>
          <PopupMenuButton
            onClick={onMedium}
            start={
              value === ImpactLevel.Medium ? <Icon icon={Check} /> : undefined
            }
            indentationLevel={value !== ImpactLevel.Medium ? 1 : 0}
          >
            {t('goal.impact.options.medium.label')}
          </PopupMenuButton>
        </PopupMenuListItem>
        <PopupMenuListItem>
          <PopupMenuButton
            onClick={onLow}
            start={
              value === ImpactLevel.Low ? <Icon icon={Check} /> : undefined
            }
            indentationLevel={value !== ImpactLevel.Low ? 1 : 0}
          >
            {t('goal.impact.options.low.label')}
          </PopupMenuButton>
        </PopupMenuListItem>
        <PopupMenuListItem>
          <PopupMenuButton
            onClick={onNone}
            start={value === null ? <Icon icon={Check} /> : undefined}
            indentationLevel={value !== null ? 1 : 0}
          >
            {t('goal.impact.options.none.label')}
          </PopupMenuButton>
        </PopupMenuListItem>
      </PopupMenuList>
    </PopupMenu>
  );
};
